import { createContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { fetchSession, requestSignOut, signInUrl, type Session } from "./session";

/**
 * Holds the answer to "who is this?" for the whole app — one request, not one
 * per component.
 *
 * Takes the place `Auth0Provider` occupied in `main.tsx`. It is deliberately
 * the *only* thing that calls `/auth/session`: a hook that fetched on mount
 * would fire once per consumer and produce four requests per page load, and
 * they could disagree.
 */

export interface SessionValue {
  session: Session;
  signIn: (returnTo?: string) => void;
  signOut: () => void;
}

export const SessionContext = createContext<SessionValue | undefined>(undefined);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session>({ status: "loading" });

  useEffect(() => {
    let live = true;
    fetchSession()
      .then((next) => {
        // The component can unmount before the request lands - in StrictMode
        // it reliably does, twice. Setting state then is a warning nobody can
        // act on.
        if (live) setSession(next);
      })
      .catch((error) => {
        /*
         * A network failure is not a signed-out visitor, but there is nothing
         * else the app can render. Logged rather than swallowed, because
         * "signed in, request failed" and "signed out" look identical from
         * the outside - the same confusion that cost a debugging round when
         * the Auth0 token renewal failed silently.
         */
        console.error("[auth] could not reach /auth/session", error);
        if (live) setSession({ status: "signed-out" });
      });
    return () => {
      live = false;
    };
  }, []);

  const value = useMemo<SessionValue>(
    () => ({
      session,
      // A top-level navigation, because OAuth needs one. Nothing after this
      // line runs.
      signIn: (returnTo?: string) => window.location.assign(signInUrl(returnTo)),
      signOut: () => {
        void requestSignOut();
      },
    }),
    [session]
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
